import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  TextInput,
  FlatList,
  Modal as RNModal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { Header, FormHeader, ScreenLayout, ConfirmationModal, colors, spacing, typography } from '../components';
import { BulkContactMapperScreen } from '../components/BulkContactMapperScreen';
import { VisitsList } from '../components/VisitsList';
import { VisitOverrideEditor, VisitOverrideValues, cleanOverrideGroup } from '../components/VisitOverrideEditor';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService, BusinessUser, DataListWebhookFieldMapping, VisitCampaign, VisitCampaignStatus } from '../services/api';
import { Department } from '../components/BusinessUserPicker';
import { usePermissions } from '../hooks/usePermissions';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface ContactSearchOption {
  contactId: string;
  name: string;
  phone?: string;
}

interface SelectedContact {
  contactId: string;
  name: string;
}

const statusColor = (status: VisitCampaignStatus): string => {
  switch (status) {
    case 'active':
      return colors.status.success as string;
    case 'completed':
      return colors.status.info as string;
    case 'cancelled':
      return colors.status.error as string;
    case 'paused':
      return colors.status.warning as string;
    default:
      return colors.text.secondary as string;
  }
};

export const VisitCampaignDetailScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, routeParams, showNotification, activeTab, setActiveTab } = useAppContext();
  const { t } = useLocale();
  const { hasPermissionAtLeast } = usePermissions();
  // Server enforces Admin for DELETE /campaigns/:id (the actual endpoint
  // "Cancel Campaign" below calls) — gating the button to match avoids a
  // confusing tap-then-403 for an Edit-tier caller who can otherwise fully
  // manage this campaign.
  const canDeleteCampaign = hasPermissionAtLeast('visits.manageCampaigns', 'admin');
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const campaignId: string | undefined = routeParams?.campaignId;

  const [campaign, setCampaign] = useState<VisitCampaign | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [departmentNames, setDepartmentNames] = useState<Record<string, string>>({});
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  // Visits actually created for this campaign so far — distinct from
  // campaign.contactCount (contacts targeted), used for the "N / M visits
  // created" KPI below. -1 = not loaded yet (avoids a flash of "0/50").
  const [visitsCreatedCount, setVisitsCreatedCount] = useState<number>(-1);

  const [isActivating, setIsActivating] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Post-creation override edit — the overrides set at creation (or left
  // unset) can now be viewed/changed here too, not just at creation time.
  const [overrides, setOverrides] = useState<VisitOverrideValues>({});
  const [isSavingOverrides, setIsSavingOverrides] = useState(false);

  const [showAddContacts, setShowAddContacts] = useState(false);
  const [addContactsTab, setAddContactsTab] = useState<'select' | 'upload'>('select');
  const [selectedContacts, setSelectedContacts] = useState<SelectedContact[]>([]);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactQuery, setContactQuery] = useState('');
  const [contactResults, setContactResults] = useState<ContactSearchOption[]>([]);
  const [isSearchingContacts, setIsSearchingContacts] = useState(false);
  const [isAttachingContacts, setIsAttachingContacts] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [pickedFile, setPickedFile] = useState<{ uri: string; name: string; mimeType: string } | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showMapper, setShowMapper] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const loadCampaign = useCallback(async () => {
    if (!campaignId) return;
    setIsLoading(true);
    try {
      const res = await apiService.getVisitCampaign(campaignId);
      if (res.statusCode === 1000 && res.data) {
        setCampaign(res.data);
        setOverrides({
          location: res.data.locationOverride,
          photos: res.data.photosOverride,
          calendar: res.data.calendarOverride,
        });

        if (res.data.assignedDepartmentIds?.length) {
          const deptRes = await apiService.getDepartments(res.data.businessId);
          if (deptRes.statusCode === 1000 && Array.isArray(deptRes.data)) {
            const map: Record<string, string> = {};
            (deptRes.data as Department[]).forEach((d) => { map[d._id] = d.name; });
            setDepartmentNames(map);
          }
        }
        if (res.data.assignedUserIds?.length) {
          const userRes = await apiService.getBusinessUsers(res.data.businessId);
          if (userRes.statusCode === 1000 && Array.isArray(userRes.data)) {
            const map: Record<string, string> = {};
            (userRes.data as BusinessUser[]).forEach((u) => {
              const first = u.user?.firstName || '';
              const last = u.user?.lastName || '';
              map[u.userId] = (first || last) ? `${first} ${last}`.trim() : (u.user?.email || u.userId);
            });
            setUserNames(map);
          }
        }
      }
    } catch (err) {
      console.warn('[VisitCampaignDetail] loadCampaign error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  useEffect(() => {
    if (!campaignId) return;
    apiService.listVisits({ campaignId })
      .then((res) => setVisitsCreatedCount(Array.isArray(res.data) ? res.data.length : 0))
      .catch((err) => { console.warn('[VisitCampaignDetail] loadVisitsCount error:', err); setVisitsCreatedCount(0); });
  }, [campaignId]);

  const searchContacts = useCallback(async (text: string) => {
    setIsSearchingContacts(true);
    try {
      // hasLocationOrAddress: same fix as VisitCampaignCreateScreen's
      // picker — a contact with no location/address can never actually
      // have a visit created for it, so it shouldn't be selectable here.
      const res = await apiService.getParentContacts({ name: text || undefined, limit: 25, hasLocationOrAddress: true });
      const raw: any[] = (res.data as any)?.contacts || (Array.isArray(res.data) ? res.data : []) || [];
      setContactResults(raw.map((c: any) => ({
        contactId: String(c._id),
        name: c.profile?.name || c.profile?.phone || '',
        phone: c.profile?.phone,
      })));
    } catch (err) {
      console.warn('[VisitCampaignDetail] searchContacts error:', err);
      setContactResults([]);
    } finally {
      setIsSearchingContacts(false);
    }
  }, []);

  useEffect(() => {
    if (!showContactPicker) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchContacts(contactQuery), contactQuery ? 300 : 0);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [contactQuery, showContactPicker, searchContacts]);

  const toggleContact = (option: ContactSearchOption) => {
    setSelectedContacts((prev) => {
      if (prev.some((c) => c.contactId === option.contactId)) {
        return prev.filter((c) => c.contactId !== option.contactId);
      }
      return [...prev, { contactId: option.contactId, name: option.name }];
    });
  };

  const isContactSelected = (contactId: string) => selectedContacts.some((c) => c.contactId === contactId);

  const handleActivate = async () => {
    if (!campaignId) return;
    setIsActivating(true);
    try {
      const res = await apiService.activateVisitCampaign(campaignId);
      if (res.statusCode === 1000) {
        showNotification('success', t('visitCampaigns.detail.title'), t('visitCampaigns.detail.activated'));
        loadCampaign();
      } else {
        showNotification('error', t('visitCampaigns.detail.title'), res.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.detail.title'), err?.message || t('visits.errors.generic'));
    } finally {
      setIsActivating(false);
    }
  };

  const handleCancel = async () => {
    if (!campaignId) return;
    setIsCancelling(true);
    try {
      const res = await apiService.cancelVisitCampaign(campaignId);
      if (res.statusCode === 1000) {
        setShowCancelConfirm(false);
        loadCampaign();
      } else {
        showNotification('error', t('visitCampaigns.detail.title'), res.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.detail.title'), err?.message || t('visits.errors.generic'));
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSaveOverrides = async () => {
    if (!campaignId) return;
    setIsSavingOverrides(true);
    try {
      const patch: Record<string, any> = {
        locationOverride: cleanOverrideGroup(overrides.location) ?? null,
        photosOverride: cleanOverrideGroup(overrides.photos) ?? null,
        calendarOverride: cleanOverrideGroup(overrides.calendar) ?? null,
      };
      const res = await apiService.updateVisitCampaign(campaignId, patch);
      if (res.statusCode === 1000) {
        showNotification('success', t('visitCampaigns.detail.title'), t('visitOverride.saved'));
        loadCampaign();
      } else {
        showNotification('error', t('visitCampaigns.detail.title'), res.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.detail.title'), err?.message || t('visits.errors.generic'));
    } finally {
      setIsSavingOverrides(false);
    }
  };

  const attachSelectedContacts = async () => {
    if (!campaignId || selectedContacts.length === 0) return;
    setIsAttachingContacts(true);
    try {
      const res = await apiService.attachVisitCampaignContacts(campaignId, selectedContacts.map((c) => c.contactId));
      if (res.statusCode === 1000) {
        showNotification('success', t('visitCampaigns.detail.addContacts'), t('visitCampaigns.create.contactsAttached', { count: res.data?.attached ?? selectedContacts.length }));
        setSelectedContacts([]);
        setShowAddContacts(false);
        loadCampaign();
      } else {
        showNotification('error', t('visitCampaigns.detail.addContacts'), res.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.detail.addContacts'), err?.message || t('visits.errors.generic'));
    } finally {
      setIsAttachingContacts(false);
    }
  };

  const handlePickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'text/csv',
          'text/comma-separated-values',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ],
        copyToCacheDirectory: true,
      });
      if (result.canceled) return;
      const file = result.assets[0];
      if (!file) return;

      setIsUploading(true);
      const mimeType = file.mimeType || 'text/csv';
      const response = await apiService.previewBulkContactUpload(file.uri, file.name, mimeType);
      if (response.statusCode === 1000 && response.data) {
        setPickedFile({ uri: file.uri, name: file.name, mimeType });
        setPreviewData(response.data);
        setShowMapper(true);
      } else {
        throw new Error(response.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.create.uploadFileTab'), err?.message || t('visits.errors.generic'));
    } finally {
      setIsUploading(false);
    }
  };

  const pollUploadJob = async (jobId: string) => {
    const POLL_INTERVAL_MS = 3000;
    const MAX_ATTEMPTS = 200;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      let statusResponse;
      try {
        statusResponse = await apiService.getBulkContactUploadStatus(jobId);
      } catch {
        continue;
      }
      if (statusResponse.statusCode !== 1000 || !statusResponse.data) continue;

      const { status, error: jobError } = statusResponse.data;
      setUploadProgress(Math.min(95, Math.round(((attempt + 1) / MAX_ATTEMPTS) * 100)));

      if (status === 'failed') {
        showNotification('error', t('visitCampaigns.create.uploadFileTab'), jobError || t('visits.errors.generic'));
        setIsUploading(false);
        return;
      }
      if (status === 'completed') {
        setUploadProgress(100);
        if (campaignId) {
          try {
            const attachRes = await apiService.attachVisitCampaignContactsFromJob(campaignId, jobId);
            if (attachRes.statusCode === 1000) {
              showNotification('success', t('visitCampaigns.create.uploadFileTab'), t('visitCampaigns.create.contactsAttached', { count: attachRes.data?.attached ?? 0 }));
              loadCampaign();
              setShowAddContacts(false);
            } else {
              showNotification('error', t('visitCampaigns.create.uploadFileTab'), attachRes.errors?.[0]?.message || t('visits.errors.generic'));
            }
          } catch (err: any) {
            showNotification('error', t('visitCampaigns.create.uploadFileTab'), err?.message || t('visits.errors.generic'));
          }
        }
        setIsUploading(false);
        setPickedFile(null);
        setPreviewData(null);
        return;
      }
    }
    setIsUploading(false);
    showNotification('error', t('visitCampaigns.create.uploadFileTab'), t('visitCampaigns.create.uploadStillProcessing'));
  };

  const handleConfirmMapping = async (mapping: DataListWebhookFieldMapping[]) => {
    if (!pickedFile) return;
    setIsUploading(true);
    try {
      const res = await apiService.commitBulkContactUpload(pickedFile.uri, pickedFile.name, pickedFile.mimeType, mapping, 'whatsapp');
      if (res.statusCode === 1000 && res.data?.jobId) {
        setShowMapper(false);
        setUploadProgress(0);
        pollUploadJob(res.data.jobId);
      } else {
        throw new Error(res.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.create.uploadFileTab'), err?.message || t('visits.errors.generic'));
      setIsUploading(false);
    }
  };

  const formatDate = (iso: string): string => {
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  };

  if (isLoading || !campaign) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        showRightPane={false}
        formHeader={<FormHeader title={t('visitCampaigns.detail.title')} onBack={() => navigateTo('visits-campaigns')} />}
      >
        <View style={styles.screen}>
          <View style={styles.centered}>
            <ActivityIndicator color={colors.primary as string} />
          </View>
        </View>
      </ScreenLayout>
    );
  }

  const isTerminal = campaign.status === 'completed' || campaign.status === 'cancelled';
  const showDistributeButton = campaign.assignedDepartmentIds.length > 0;

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
      formHeader={<FormHeader title={campaign.name} onBack={() => navigateTo('visits-campaigns')} />}
    >
      <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.formCard}>
          <View style={styles.headerRow}>
            <Text style={styles.campaignName}>{campaign.name}</Text>
            <View style={[styles.statusBadge, { borderColor: statusColor(campaign.status) }]}>
              <Text style={[styles.statusBadgeText, { color: statusColor(campaign.status) }]}>
                {t(`visitCampaigns.status.${campaign.status}` as any)}
              </Text>
            </View>
          </View>
          {campaign.description ? <Text style={styles.description}>{campaign.description}</Text> : null}

          <View style={styles.metaRow}>
            <Ionicons name="calendar-outline" size={14} color={colors.text.secondary as string} />
            <Text style={styles.metaText}>{formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}</Text>
          </View>
          <View style={styles.metaRow}>
            <Ionicons name="people-outline" size={14} color={colors.text.secondary as string} />
            <Text style={styles.metaText}>{t('visitCampaigns.list.contactCount', { count: campaign.contactCount })}</Text>
          </View>

          {/* Analytics KPIs (Issue D) — visits actually created vs contacts
              targeted, estimated total field time, and daily-target pacing.
              Previously only contactCount was shown here; none of these
              numbers (despite expectedDurationMinutes/dailyVisitTarget
              already existing on the campaign entity) were ever surfaced. */}
          <View style={styles.kpiRow}>
            <View style={styles.kpiTile}>
              <Text style={styles.kpiValue}>
                {visitsCreatedCount >= 0 ? `${visitsCreatedCount} / ${campaign.contactCount}` : '—'}
              </Text>
              <Text style={styles.kpiLabel}>{t('visitCampaigns.detail.kpiVisitsCreated')}</Text>
            </View>
            {!!campaign.expectedDurationMinutes && (
              <View style={styles.kpiTile}>
                <Text style={styles.kpiValue}>
                  {(() => {
                    const totalMinutes = campaign.expectedDurationMinutes! * campaign.contactCount;
                    const hours = totalMinutes / 60;
                    return hours >= 1 ? t('visitCampaigns.detail.kpiHours', { hours: Math.round(hours * 10) / 10 }) : `${totalMinutes} ${t('visits.detail.minutes')}`;
                  })()}
                </Text>
                <Text style={styles.kpiLabel}>{t('visitCampaigns.detail.kpiEstimatedTime')}</Text>
              </View>
            )}
            {!!campaign.dailyVisitTarget && (
              <View style={styles.kpiTile}>
                <Text style={styles.kpiValue}>
                  {t('visitCampaigns.detail.kpiDaysValue', { days: Math.ceil(campaign.contactCount / campaign.dailyVisitTarget!) })}
                </Text>
                <Text style={styles.kpiLabel}>{t('visitCampaigns.detail.kpiDailyTarget', { target: campaign.dailyVisitTarget })}</Text>
              </View>
            )}
          </View>

          {campaign.assignedDepartmentIds.length > 0 && (
            <View style={styles.metaRow}>
              <Ionicons name="business-outline" size={14} color={colors.text.secondary as string} />
              <Text style={styles.metaText}>
                {campaign.assignedDepartmentIds.map((id) => departmentNames[id] || id).join(', ')}
              </Text>
            </View>
          )}
          {campaign.assignedUserIds.length > 0 && (
            <View style={styles.metaRow}>
              <Ionicons name="person-outline" size={14} color={colors.text.secondary as string} />
              <Text style={styles.metaText}>
                {campaign.assignedUserIds.map((id) => userNames[id] || id).join(', ')}
              </Text>
            </View>
          )}

          {/* Post-creation override view/edit — previously the overrides
              set (or left unset) at creation had no surface to view or
              change afterward. */}
          {hasPermissionAtLeast('visits.manageCampaigns', 'edit') && (
            <View style={styles.overrideSectionWrap}>
              <VisitOverrideEditor value={overrides} onChange={setOverrides} />
              <TouchableOpacity
                style={[styles.saveOverridesButton, isSavingOverrides && styles.disabledButton]}
                onPress={handleSaveOverrides}
                disabled={isSavingOverrides}
              >
                {isSavingOverrides ? (
                  <ActivityIndicator color={colors.white as string} />
                ) : (
                  <Text style={styles.saveOverridesButtonText}>{t('visitOverride.save')}</Text>
                )}
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.actionsRow}>
            {campaign.status === 'draft' && (
              <TouchableOpacity style={[styles.primaryButtonFlex, isActivating && styles.disabledButton]} onPress={handleActivate} disabled={isActivating}>
                {isActivating ? <ActivityIndicator color={colors.white as string} /> : <Text style={styles.primaryButtonText}>{t('visitCampaigns.detail.activate')}</Text>}
              </TouchableOpacity>
            )}
            {!isTerminal && canDeleteCampaign && (
              <TouchableOpacity style={styles.dangerButtonFlex} onPress={() => setShowCancelConfirm(true)}>
                <Text style={styles.dangerButtonText}>{t('visitCampaigns.detail.cancelCampaign')}</Text>
              </TouchableOpacity>
            )}
          </View>

          <View style={styles.actionsRow}>
            <TouchableOpacity style={styles.secondaryButtonFlex} onPress={() => setShowAddContacts(true)}>
              <Text style={styles.secondaryButtonText}>{t('visitCampaigns.detail.addContacts')}</Text>
            </TouchableOpacity>
            {/* Route entry point at the campaign level — previously only
                reachable from the main Visits list with no campaign
                pre-filter. */}
            <TouchableOpacity
              style={styles.secondaryButtonFlex}
              onPress={() => navigateTo('visit-route', undefined, { campaignId: campaign._id })}
            >
              <Text style={styles.secondaryButtonText}>{t('visitCampaigns.detail.route')}</Text>
            </TouchableOpacity>
            {showDistributeButton ? (
              <TouchableOpacity
                style={styles.secondaryButtonFlex}
                onPress={() => navigateTo('visit-campaign-distribute', undefined, { campaignId: campaign._id })}
              >
                <Text style={styles.secondaryButtonText}>{t('visitCampaigns.detail.distribute')}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          {!showDistributeButton && campaign.assignedUserIds.length > 0 && (
            <Text style={styles.autoDistributedNote}>{t('visitCampaigns.detail.autoDistributedNote')}</Text>
          )}
        </View>

        <View style={styles.visitsListWrap}>
          <Text style={styles.sectionTitle}>{t('visitCampaigns.detail.visitsSection')}</Text>
          <VisitsList campaignId={campaign._id} />
        </View>
      </ScrollView>
      </View>

      <ConfirmationModal
        visible={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleCancel}
        title={t('visitCampaigns.detail.cancelConfirmTitle')}
        message={t('visitCampaigns.detail.cancelConfirmMessage')}
        type="warning"
        loading={isCancelling}
      />

      <RNModal visible={showAddContacts} transparent animationType="fade" onRequestClose={() => setShowAddContacts(false)}>
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={() => setShowAddContacts(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.addContactsCard} onPress={() => {}}>
            <Text style={styles.pickerTitle}>{t('visitCampaigns.detail.addContacts')}</Text>
            <View style={styles.segmentedRow}>
              <TouchableOpacity
                style={[styles.segmentButton, addContactsTab === 'select' && styles.segmentButtonActive]}
                onPress={() => setAddContactsTab('select')}
              >
                <Text style={[styles.segmentButtonText, addContactsTab === 'select' && styles.segmentButtonTextActive]}>
                  {t('visitCampaigns.create.selectContactsTab')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.segmentButton, addContactsTab === 'upload' && styles.segmentButtonActive]}
                onPress={() => setAddContactsTab('upload')}
              >
                <Text style={[styles.segmentButtonText, addContactsTab === 'upload' && styles.segmentButtonTextActive]}>
                  {t('visitCampaigns.create.uploadFileTab')}
                </Text>
              </TouchableOpacity>
            </View>

            {addContactsTab === 'select' ? (
              <>
                <TouchableOpacity style={styles.pickerButton} onPress={() => { setContactQuery(''); setShowContactPicker(true); }}>
                  <Text style={styles.pickerButtonText}>{t('visitCampaigns.create.searchAndSelectContacts')}</Text>
                </TouchableOpacity>
                {selectedContacts.length > 0 && (
                  <View style={styles.selectedChipsWrap}>
                    {selectedContacts.map((c) => (
                      <View key={c.contactId} style={styles.selectedChip}>
                        <Text style={styles.selectedChipText} numberOfLines={1}>{c.name}</Text>
                        <TouchableOpacity onPress={() => toggleContact({ contactId: c.contactId, name: c.name })}>
                          <Ionicons name="close-circle" size={16} color={colors.text.secondary as string} />
                        </TouchableOpacity>
                      </View>
                    ))}
                  </View>
                )}
                <TouchableOpacity
                  style={[styles.primaryButton, (selectedContacts.length === 0 || isAttachingContacts) && styles.disabledButton]}
                  onPress={attachSelectedContacts}
                  disabled={selectedContacts.length === 0 || isAttachingContacts}
                >
                  {isAttachingContacts ? <ActivityIndicator color={colors.white as string} /> : <Text style={styles.primaryButtonText}>{t('visitCampaigns.create.attachSelected', { count: selectedContacts.length })}</Text>}
                </TouchableOpacity>
              </>
            ) : (
              <>
                <TouchableOpacity style={[styles.pickerButton, isUploading && styles.disabledButton]} onPress={handlePickFile} disabled={isUploading}>
                  <Text style={styles.pickerButtonText}>{t('visitCampaigns.create.chooseFile')}</Text>
                </TouchableOpacity>
                {isUploading && (
                  <View style={styles.uploadProgressBox}>
                    <ActivityIndicator color={colors.primary as string} />
                    <Text style={styles.metaText}>{t('visitCampaigns.create.uploadingProgress', { percent: uploadProgress })}</Text>
                  </View>
                )}
              </>
            )}
            <TouchableOpacity style={styles.secondaryButton} onPress={() => setShowAddContacts(false)}>
              <Text style={styles.secondaryButtonText}>{t('visits.common.cancel')}</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        </TouchableOpacity>
      </RNModal>

      <RNModal visible={showContactPicker} transparent animationType="fade" onRequestClose={() => setShowContactPicker(false)}>
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={() => setShowContactPicker(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.pickerCard} onPress={() => {}}>
            <Text style={styles.pickerTitle}>{t('visitCampaigns.create.searchAndSelectContacts')}</Text>
            <TextInput
              style={styles.searchInput}
              placeholder={t('visits.create.searchContacts')}
              value={contactQuery}
              onChangeText={setContactQuery}
              autoCapitalize="none"
            />
            {isSearchingContacts ? (
              <View style={styles.loadingBox}>
                <ActivityIndicator color={colors.primary as string} />
              </View>
            ) : (
              <FlatList
                data={contactResults}
                keyExtractor={(c) => c.contactId}
                style={styles.pickerList}
                keyboardShouldPersistTaps="handled"
                ListEmptyComponent={<Text style={styles.emptyText}>{t('visits.create.noContactsFound')}</Text>}
                renderItem={({ item }) => {
                  const selected = isContactSelected(item.contactId);
                  return (
                    <TouchableOpacity style={styles.contactOption} onPress={() => toggleContact(item)}>
                      <Ionicons
                        name={selected ? 'checkbox' : 'square-outline'}
                        size={20}
                        color={selected ? (colors.primary as string) : (colors.text.secondary as string)}
                      />
                      <View style={styles.contactOptionTextWrap}>
                        <Text style={styles.contactOptionName}>{item.name}</Text>
                        {item.phone ? <Text style={styles.metaText}>{item.phone}</Text> : null}
                      </View>
                    </TouchableOpacity>
                  );
                }}
              />
            )}
            <TouchableOpacity style={styles.primaryButton} onPress={() => setShowContactPicker(false)}>
              <Text style={styles.primaryButtonText}>{t('visitCampaigns.create.done')}</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        </TouchableOpacity>
      </RNModal>

      {previewData && (
        <BulkContactMapperScreen
          visible={showMapper}
          onClose={() => setShowMapper(false)}
          headers={previewData.headers}
          totalRows={previewData.totalRows}
          targetFields={previewData.targetFields}
          suggestedMapping={previewData.suggestedMapping}
          fromRememberedMapping={previewData.fromRememberedMapping}
          committing={isUploading}
          onConfirm={handleConfirmMapping}
        />
      )}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: { flex: 1 },
  content: { padding: spacing.lg, paddingBottom: spacing.xl * 2 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },

  formCard: {
    backgroundColor: colors.background.surface as string,
    borderRadius: 12,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: colors.border as string,
    marginBottom: spacing.lg,
  },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs },
  campaignName: { flex: 1, fontSize: typography.fontSize.xl, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string, marginRight: spacing.sm },
  description: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginBottom: spacing.sm },
  statusBadge: { borderWidth: 1, borderRadius: 12, paddingVertical: 2, paddingHorizontal: spacing.sm },
  statusBadgeText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold },

  metaRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.xs },
  metaText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, flex: 1 },

  kpiRow: { flexDirection: 'row', gap: spacing.sm, marginVertical: spacing.md },
  kpiTile: {
    flex: 1,
    backgroundColor: colors.background.secondary as string,
    borderRadius: 10,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    alignItems: 'center',
  },
  kpiValue: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.bold, color: colors.primary as string },
  kpiLabel: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, textAlign: 'center', marginTop: 2 },
  overrideSectionWrap: { marginTop: spacing.md, paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: colors.border as string },
  saveOverridesButton: { backgroundColor: colors.primary as string, borderRadius: 8, paddingVertical: spacing.sm, alignItems: 'center', marginTop: spacing.sm },
  saveOverridesButtonText: { color: colors.white as string, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold },

  actionsRow: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.md },
  primaryButtonFlex: { flex: 1, backgroundColor: colors.primary as string, borderRadius: 12, paddingVertical: spacing.md, alignItems: 'center' },
  primaryButtonText: { color: colors.white as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  secondaryButtonFlex: { flex: 1, borderWidth: 1, borderColor: colors.primary as string, borderRadius: 12, paddingVertical: spacing.md, alignItems: 'center' },
  secondaryButtonText: { color: colors.primary as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  dangerButtonFlex: { flex: 1, borderWidth: 1, borderColor: colors.status.error as string, borderRadius: 12, paddingVertical: spacing.md, alignItems: 'center' },
  dangerButtonText: { color: colors.status.error as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  disabledButton: { opacity: 0.6 },
  autoDistributedNote: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginTop: spacing.sm, fontStyle: 'italic' },

  sectionTitle: { fontSize: typography.fontSize.xl, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string, marginBottom: spacing.sm, paddingHorizontal: spacing.lg },
  visitsListWrap: { flex: 1, minHeight: 300 },

  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', alignItems: 'center', justifyContent: 'center' },
  addContactsCard: {
    width: '90%',
    maxWidth: 420,
    backgroundColor: colors.background.surface as string,
    borderRadius: 14,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  pickerCard: {
    width: '90%',
    maxWidth: 380,
    maxHeight: '80%',
    backgroundColor: colors.background.surface as string,
    borderRadius: 14,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  pickerTitle: { fontSize: typography.fontSize.xl, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  searchInput: {
    height: 40,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.background.secondary as string,
    color: colors.text.primary as string,
    fontSize: typography.fontSize.sm,
  },
  loadingBox: { paddingVertical: spacing.lg, alignItems: 'center' },
  pickerList: { maxHeight: 320 },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary as string, textAlign: 'center', paddingVertical: spacing.lg },
  contactOption: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border as string },
  contactOptionTextWrap: { flex: 1 },
  contactOptionName: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },

  segmentedRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  segmentButton: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  segmentButtonActive: { backgroundColor: colors.primary as string, borderColor: colors.primary as string },
  segmentButtonText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  segmentButtonTextActive: { color: colors.white as string, fontFamily: typography.fontFamily.semibold },

  pickerButton: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  pickerButtonText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },

  selectedChipsWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  selectedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.background.secondary as string,
    borderRadius: 14,
    paddingVertical: 4,
    paddingHorizontal: spacing.sm,
    maxWidth: 160,
  },
  selectedChipText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string, flexShrink: 1 },

  primaryButton: { backgroundColor: colors.primary as string, borderRadius: 12, paddingVertical: spacing.md, alignItems: 'center', marginTop: spacing.sm },
  secondaryButton: {
    borderWidth: 1,
    borderColor: colors.primary as string,
    borderRadius: 12,
    paddingVertical: spacing.md,
    alignItems: 'center',
    marginTop: spacing.sm,
  },

  uploadProgressBox: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.sm },
});
